import { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Button,
    Paper,
    Breadcrumbs,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Tabs,
    Tab,
} from '@mui/material';
import { useNotification } from '../../../components/Notification';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    DirectionsCar as DirectionsCarIcon,
    NavigateNext as NavigateNextIcon,
    Category as CategoryIcon,
} from '@mui/icons-material';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import AddVehicleModal from './AddVehicleModal';
import VehicleTypes from '../VehicleTypes';
import AddVehicleTypeModal from '../VehicleTypes/AddVehicleTypeModal';
import { hasPermission } from '../../../utils/permissions';
import {
    bulkDeleteVehicleTypes,
    addVehicleType,
} from '../VehicleTypes/slice';
import {
    fetchInit,
    fetchVehicles,
    fetchVehicleById,
    addVehicle,
    updateVehicle,
    selectVehicles,
    selectUnits,
    selectVehicleTypesForVehicles,
    selectVehiclesLoading,
    selectVehiclesError,
    selectVehiclesSuccessMessage,
    selectVehiclesTotal,
    selectVehiclesPage,
    selectVehiclesPageSize,
    selectVehiclesSortColumn,
    selectVehiclesSortDirection,
    selectVehiclesFilterName,
    selectSelectedVehicle,
    setPage,
    setPageSize,
    setSortColumn,
    setSortDirection,
    setFilterName,
    deleteVehicle,
    bulkDeleteVehicles,
    clearError,
    clearSuccessMessage,
} from './slice';
import './Vehicles.css';

// TabPanel component - defined outside to prevent re-creation on each render
function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vehicles-tabpanel-${index}`}
            aria-labelledby={`vehicles-tab-${index}`}
            style={{ flex: 1, display: value === index ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden' }}
            {...other}
        >
            <Box sx={{ pt: 2, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>{children}</Box>
        </div>
    );
}

function Vehicles() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const vehicles = useSelector(selectVehicles);
    const units = useSelector(selectUnits);
    const vehicleTypes = useSelector(selectVehicleTypesForVehicles);
    const loading = useSelector(selectVehiclesLoading);
    const error = useSelector(selectVehiclesError);
    const successMessage = useSelector(selectVehiclesSuccessMessage);
    const total = useSelector(selectVehiclesTotal);
    const page = useSelector(selectVehiclesPage);
    const pageSize = useSelector(selectVehiclesPageSize);
    const sortColumn = useSelector(selectVehiclesSortColumn);
    const sortDirection = useSelector(selectVehiclesSortDirection);
    const filterName = useSelector(selectVehiclesFilterName);
    const selectedVehicle = useSelector(selectSelectedVehicle);

    const [selectedIds, setSelectedIds] = useState([]);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [vehicleToDelete, setVehicleToDelete] = useState(null);
    const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    // Check permissions - assuming 'vehicle' module with standard CRUD permissions
    const canAddVehicle = hasPermission('vehicle', 'addVehicle');
    const canEditVehicle = hasPermission('vehicle', 'editVehicle');
    const canDeleteVehicle = hasPermission('vehicle', 'deleteVehicle');

    // VehicleTypes permissions
    const canAddVehicleType = hasPermission('vehicletype', 'addVehicleType');
    const canDeleteVehicleType = hasPermission('vehicletype', 'deleteVehicleType');

    // State for VehicleTypes buttons
    const [vehicleTypesSelectedIds, setVehicleTypesSelectedIds] = useState([]);
    const [showVehicleTypeAddDialog, setShowVehicleTypeAddDialog] = useState(false);
    const [showVehicleTypeBulkDeleteDialog, setShowVehicleTypeBulkDeleteDialog] = useState(false);

    // Handlers for VehicleTypes - use useCallback to prevent re-creation
    const handleAddVehicleType = useCallback(() => {
        setShowVehicleTypeAddDialog(true);
    }, []);

    const handleCloseAddVehicleType = useCallback(() => {
        setShowVehicleTypeAddDialog(false);
    }, []);

    const handleConfirmAddVehicleType = useCallback((vehicleTypeData) => {
        dispatch(addVehicleType(vehicleTypeData));
        setShowVehicleTypeAddDialog(false);
    }, [dispatch]);

    const handleBulkDeleteVehicleTypes = useCallback(() => {
        setShowVehicleTypeBulkDeleteDialog(true);
    }, []);

    // Memoize the setter to prevent re-creation
    const handleVehicleTypesSelectedIdsChange = useCallback((ids) => {
        setVehicleTypesSelectedIds(ids);
    }, []);

    // Fetch init data on mount
    useEffect(() => {
        dispatch(fetchInit());
    }, [dispatch]);

    // Fetch vehicles whenever tab changes or filters change
    useEffect(() => {
        dispatch(fetchVehicles({ page, pageSize, sortColumn, sortDirection, filterName }));
    }, [dispatch, page, pageSize, sortColumn, sortDirection, filterName, activeTab]);

    // Show error notification
    useEffect(() => {
        if (error) {
            showNotification(error, 'error');
            dispatch(clearError());
        }
    }, [error, dispatch, showNotification]);

    // Show success notification
    useEffect(() => {
        if (successMessage) {
            showNotification(successMessage, 'success');
            dispatch(clearSuccessMessage());
            // Refetch vehicles to update the list
            dispatch(fetchVehicles({ page, pageSize, sortColumn, sortDirection, filterName }));
        }
    }, [successMessage, dispatch, showNotification, page, pageSize, sortColumn, sortDirection, filterName]);

    // Define table columns
    const columns = [
        {
            key: 'vehicleTypeId',
            label: t('vehicles.table.vehicleType'),
            render: (value) => {
                // Find the vehicle type name from vehicleTypes array
                const vehicleType = vehicleTypes.find((vt) => vt.id === value);
                return vehicleType ? vehicleType.name : '-';
            },
        },
        {
            key: 'licensePlate',
            label: t('vehicles.table.licensePlate'),
        },
        {
            key: 'unitId',
            label: t('vehicles.table.unit'),
            render: (value) => {
                // value is the unit ID (number)
                // Find the unit object from units array
                const unitObj = units.find((unit) => unit.id === value);
                const unitName = unitObj ? unitObj.name : '-';

                return (
                    <Box
                        component="span"
                        sx={{
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            backgroundColor: unitObj ? '#e3f2fd' : '#e2e8f0',
                            color: unitObj ? '#1565c0' : '#64748b',
                        }}
                    >
                        {unitName}
                    </Box>
                );
            },
        },
        {
            key: 'identificationInfo',
            label: t('vehicles.table.identificationInfo'),
        },
    ];

    // Handle edit action
    const handleEdit = (row) => {
        // Fetch vehicle details by ID
        dispatch(fetchVehicleById(row.id));
        setShowEditDialog(true);
    };

    // Handle delete action - show confirmation dialog
    const handleDelete = (row) => {
        setVehicleToDelete(row);
        setShowDeleteDialog(true);
    };

    // Confirm delete action
    const handleConfirmDelete = () => {
        if (vehicleToDelete?.id) {
            dispatch(deleteVehicle(vehicleToDelete.id));
        }
        setShowDeleteDialog(false);
        setVehicleToDelete(null);
    };

    // Cancel delete action
    const handleCancelDelete = () => {
        setShowDeleteDialog(false);
        setVehicleToDelete(null);
    };

    // Handle bulk delete - show confirmation dialog
    const handleBulkDelete = () => {
        setShowBulkDeleteDialog(true);
    };

    // Confirm bulk delete action
    const handleConfirmBulkDelete = () => {
        dispatch(bulkDeleteVehicles(selectedIds));
        setShowBulkDeleteDialog(false);
        setSelectedIds([]);
    };

    // Cancel bulk delete action
    const handleCancelBulkDelete = () => {
        setShowBulkDeleteDialog(false);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        dispatch(setPage(newPage));
    };

    // Handle page size change
    const handlePageSizeChange = (newPageSize) => {
        dispatch(setPageSize(newPageSize));
    };

    // Handle sort change
    const handleSortChange = (column, direction) => {
        dispatch(setSortColumn(column));
        dispatch(setSortDirection(direction));
        // fetchVehicles will be called automatically by useEffect
    };

    // Handle filter name change
    const handleFilterNameChange = (event) => {
        dispatch(setFilterName(event.target.value));
    };

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        // Reset selectedIds when switching tabs
        setSelectedIds([]);
        setVehicleTypesSelectedIds([]);
        // Refetch init data to update vehicleTypes for select in modal
        dispatch(fetchInit());
    };

    // Handle add vehicle - show dialog
    const handleAddVehicle = () => {
        setShowAddDialog(true);
    };

    // Handle close add vehicle dialog
    const handleCloseAddVehicle = () => {
        setShowAddDialog(false);
    };

    // Confirm add vehicle
    const handleConfirmAddVehicle = (vehicleData) => {
        dispatch(addVehicle(vehicleData));
        setShowAddDialog(false);
    };

    // Handle close edit vehicle dialog
    const handleCloseEditVehicle = () => {
        setShowEditDialog(false);
    };

    // Confirm edit vehicle
    const handleConfirmEditVehicle = (vehicleData) => {
        dispatch(updateVehicle(vehicleData));
        setShowEditDialog(false);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header Section */}
            <Box sx={{ px: 3, pt: 3, pb: 2, backgroundColor: '#ffffff' }}>
                {/* Breadcrumbs */}
                <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <DirectionsCarIcon fontSize="small" />
                        <Typography variant="body2">{t('vehicles.breadcrumb.catalog')}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.primary">
                        {t('vehicles.breadcrumb.vehicles')}
                    </Typography>
                </Breadcrumbs>

                {/* Page Title and Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" component="h1" fontWeight="bold" sx={{ textAlign: 'left' }}>
                        {t('vehicles.title')}
                    </Typography>

                    {/* Action Buttons - Show based on active tab */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        {activeTab === 0 && (
                            <>
                                {canAddVehicle && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<AddIcon />}
                                        onClick={handleAddVehicle}
                                        id="addVehicle"
                                        name="addVehicle"
                                        data-testid="vehicles-button-addVehicle"
                                    >
                                        {t('vehicles.addVehicle')}
                                    </Button>
                                )}
                                {canDeleteVehicle && (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={handleBulkDelete}
                                        disabled={selectedIds.length === 0}
                                        id="deleteSelected"
                                        name="deleteSelected"
                                        data-testid="vehicles-button-deleteSelected"
                                    >
                                        {t('vehicles.deleteSelected')}
                                    </Button>
                                )}
                            </>
                        )}
                        {activeTab === 1 && (
                            <>
                                {canAddVehicleType && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<AddIcon />}
                                        onClick={handleAddVehicleType}
                                        id="addVehicleType"
                                        name="addVehicleType"
                                        data-testid="vehicles-button-addVehicleType"
                                    >
                                        {t('vehicleTypes.addVehicleType')}
                                    </Button>
                                )}
                                {canDeleteVehicleType && (
                                    <Button
                                        variant="contained"
                                        color="error"
                                        startIcon={<DeleteIcon />}
                                        onClick={handleBulkDeleteVehicleTypes}
                                        disabled={vehicleTypesSelectedIds.length === 0}
                                        id="deleteSelectedVehicleTypes"
                                        name="deleteSelectedVehicleTypes"
                                        data-testid="vehicles-button-deleteSelectedVehicleTypes"
                                    >
                                        {t('vehicleTypes.deleteSelected')}
                                    </Button>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Main Content */}
            <Paper
                elevation={0}
                sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', mt: 2 }}
            >
                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
                    <Tabs value={activeTab} onChange={handleTabChange} aria-label="vehicles tabs">
                        <Tab
                            icon={<DirectionsCarIcon />}
                            iconPosition="start"
                            label={t('vehicles.tabs.vehicles')}
                            id="vehicles-tab-0"
                            aria-controls="vehicles-tabpanel-0"
                        />
                        <Tab
                            icon={<CategoryIcon />}
                            iconPosition="start"
                            label={t('vehicles.tabs.vehicleTypes')}
                            id="vehicles-tab-1"
                            aria-controls="vehicles-tabpanel-1"
                        />
                    </Tabs>
                </Box>

                {/* Vehicles Tab */}
                <TabPanel value={activeTab} index={0}>
                    {/* Filter Section */}
                    <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb' }}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Input
                                label={t('vehicles.filterByName')}
                                value={filterName}
                                onChange={handleFilterNameChange}
                                placeholder={t('vehicles.filterByNamePlaceholder')}
                                id="filterName"
                                name="filterName"
                                data-testid="vehicles-input-filterName"
                                sx={{ width: 320 }}
                            />
                        </Box>
                    </Box>

                    {/* Table */}
                    <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff', minHeight: 0 }}>
                        <Table
                            id="vehicles-table"
                            columns={columns}
                            data={vehicles}
                            selectable={true}
                            selectedIds={selectedIds}
                            onSelectionChange={setSelectedIds}
                            onEdit={canEditVehicle ? handleEdit : null}
                            onDelete={canDeleteVehicle ? handleDelete : null}
                            onRowDoubleClick={canEditVehicle ? handleEdit : null}
                            loading={loading}
                            emptyMessage={t('vehicles.noData')}
                            idField="id"
                            pagination={true}
                            page={page}
                            pageSize={pageSize}
                            totalItems={total}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                            pageSizeOptions={[5, 10, 20, 50, 100]}
                            sortable={true}
                            sortColumn={sortColumn}
                            sortDirection={sortDirection}
                            onSortChange={handleSortChange}
                            sortableColumns={['vehicleTypeId', 'licensePlate', 'unitId', 'identificationInfo']}
                        />
                    </Box>
                </TabPanel>

                {/* Vehicle Types Tab */}
                <TabPanel value={activeTab} index={1}>
                    <VehicleTypes
                        onSelectedIdsChange={handleVehicleTypesSelectedIdsChange}
                        onAddClick={handleAddVehicleType}
                        onBulkDeleteClick={handleBulkDeleteVehicleTypes}
                        hideButtons={true}
                        isActive={activeTab === 1}
                    />
                </TabPanel>
            </Paper>

            {/* Confirm Delete Dialog */}
            <Dialog open={showDeleteDialog} onClose={handleCancelDelete} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'error.main',
                    }}
                >
                    <DeleteIcon />
                    {t('vehicles.deleteDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('vehicles.deleteDialog.message', {
                            name: vehicleToDelete?.licensePlate || '',
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCancelDelete}
                        variant="outlined"
                        id="cancelDelete"
                        name="cancelDelete"
                        data-testid="vehicles-button-cancelDelete"
                    >
                        {t('vehicles.deleteDialog.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        id="confirmDelete"
                        name="confirmDelete"
                        data-testid="vehicles-button-confirmDelete"
                    >
                        {t('vehicles.deleteDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Confirm Bulk Delete Dialog */}
            <Dialog open={showBulkDeleteDialog} onClose={handleCancelBulkDelete} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'error.main',
                    }}
                >
                    <DeleteIcon />
                    {t('vehicles.bulkDeleteDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('vehicles.bulkDeleteDialog.message', {
                            count: selectedIds.length,
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleCancelBulkDelete}
                        variant="outlined"
                        id="cancelBulkDelete"
                        name="cancelBulkDelete"
                        data-testid="vehicles-button-cancelBulkDelete"
                    >
                        {t('vehicles.bulkDeleteDialog.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirmBulkDelete}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        id="confirmBulkDelete"
                        name="confirmBulkDelete"
                        data-testid="vehicles-button-confirmBulkDelete"
                    >
                        {t('vehicles.bulkDeleteDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Vehicle Modal */}
            <AddVehicleModal open={showAddDialog} onClose={handleCloseAddVehicle} onConfirm={handleConfirmAddVehicle} units={units} vehicleTypes={vehicleTypes} />

            {/* Edit Vehicle Modal */}
            <AddVehicleModal
                open={showEditDialog}
                onClose={handleCloseEditVehicle}
                onConfirm={handleConfirmEditVehicle}
                units={units}
                vehicleTypes={vehicleTypes}
                vehicleData={selectedVehicle}
                editMode={true}
            />

            {/* Add Vehicle Type Modal */}
            <AddVehicleTypeModal
                open={showVehicleTypeAddDialog}
                onClose={handleCloseAddVehicleType}
                onConfirm={handleConfirmAddVehicleType}
            />

            {/* Confirm Bulk Delete Vehicle Types Dialog */}
            <Dialog
                open={showVehicleTypeBulkDeleteDialog}
                onClose={() => setShowVehicleTypeBulkDeleteDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: 'error.main',
                    }}
                >
                    <DeleteIcon />
                    {t('vehicleTypes.bulkDeleteDialog.title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('vehicleTypes.bulkDeleteDialog.message', {
                            count: vehicleTypesSelectedIds.length,
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setShowVehicleTypeBulkDeleteDialog(false)}
                        variant="outlined"
                        id="cancelBulkDeleteVehicleTypes"
                        name="cancelBulkDeleteVehicleTypes"
                        data-testid="vehicles-button-cancelBulkDeleteVehicleTypes"
                    >
                        {t('vehicleTypes.bulkDeleteDialog.cancel')}
                    </Button>
                    <Button
                        onClick={() => {
                            dispatch(bulkDeleteVehicleTypes(vehicleTypesSelectedIds));
                            setShowVehicleTypeBulkDeleteDialog(false);
                            setVehicleTypesSelectedIds([]);
                        }}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        id="confirmBulkDeleteVehicleTypes"
                        name="confirmBulkDeleteVehicleTypes"
                        data-testid="vehicles-button-confirmBulkDeleteVehicleTypes"
                    >
                        {t('vehicleTypes.bulkDeleteDialog.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Vehicles;
